import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiSearch, FiDownload, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiCalendar, FiList } from 'react-icons/fi';
import { format } from 'date-fns';
import TaskModal from '../components/TaskModal';
import TaskCalendar from '../components/TaskCalendar';

const Tasks = () => {
  const { isAdmin, user } = useAuth();
  const { isDark } = useTheme();
  const { showSuccess, showError } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [filterType, setFilterType] = useState('all'); // 'all', 'assigned_to_me', 'assigned_by_me'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 10;
  
  // View mode: 'table' or 'calendar'
  const [viewMode, setViewMode] = useState('table');
  // Admin: 'team' (team tasks) or 'everyone_my_tasks' (everyone's personal tasks)
  const [tasksPageView, setTasksPageView] = useState('team');
  const [allUsersTasks, setAllUsersTasks] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);

  useEffect(() => {
    if (tasksPageView === 'team') fetchTasks();
  }, [searchQuery, filter, priorityFilter, selectedTags, filterType, tasksPageView]);

  useEffect(() => {
    if (isAdmin && tasksPageView === 'everyone_my_tasks') {
      setAllUsersLoading(true);
      api.get('/my-tasks/all')
        .then((res) => setAllUsersTasks(res.data.users || []))
        .catch(() => showError('Failed to load everyone\'s My Tasks'))
        .finally(() => setAllUsersLoading(false));
    }
  }, [isAdmin, tasksPageView]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filter !== 'all') params.append('status', filter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (filterType !== 'all') params.append('filter_type', filterType);
      if (selectedTags.length > 0) {
        selectedTags.forEach(tag => params.append('tags', tag));
      }

      const queryString = params.toString();
      const url = `/tasks${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      setTasks(response.data.tasks || []);
      setCurrentPage(1); // Reset to first page when filters change
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/tasks/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tasks-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccess('Tasks exported successfully!', 'Export Complete');
    } catch (error) {
      showError('Failed to export tasks', 'Export Error');
    }
  };

  // Get all unique tags from tasks
  const allTags = [...new Set(tasks.flatMap(task => {
    if (!task.tags || !Array.isArray(task.tags)) return [];
    return task.tags.map(tag => typeof tag === 'string' ? tag : (tag.tag || tag));
  }))];

  const getStatusColor = (status) => {
    const colors = {
      pending: isDark ? 'bg-gray-600' : 'bg-gray-500',
      in_progress: isDark ? 'bg-blue-600' : 'bg-blue-500',
      completed: isDark ? 'bg-green-600' : 'bg-green-500',
      blocked: isDark ? 'bg-red-600' : 'bg-red-500',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: isDark ? 'bg-green-600' : 'bg-green-500',
      medium: isDark ? 'bg-yellow-600' : 'bg-yellow-500',
      high: isDark ? 'bg-red-600' : 'bg-red-500',
    };
    return colors[priority] || colors.medium;
  };

  // Pagination calculations
  const totalPages = Math.ceil(tasks.length / tasksPerPage);
  const startIndex = (currentPage - 1) * tasksPerPage;
  const endIndex = startIndex + tasksPerPage;
  const currentTasks = tasks.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (tasksPageView === 'team' && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:gap-4">
        <div>
          <h1 className={`text-2xl lg:text-3xl font-bold mb-1 lg:mb-2 ${isDark ? 'text-white' : 'text-white'}`}>
            Tasks
          </h1>
          <p className={`text-sm lg:text-base ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
            {tasksPageView === 'everyone_my_tasks' ? "Everyone's personal tasks (admin view)" : 'Manage and track your tasks'}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTasksPageView('team')}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                tasksPageView === 'team'
                  ? isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-primary-600 shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              Team Tasks
            </button>
            <button
              onClick={() => setTasksPageView('everyone_my_tasks')}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                tasksPageView === 'everyone_my_tasks'
                  ? isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-primary-600 shadow-lg'
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              Everyone's My Tasks
            </button>
          </div>
        )}
        {tasksPageView === 'team' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowModal(true)}
            className={`px-5 py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all shadow-lg ${
              isDark 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-white text-primary-600 active:bg-white/90'
            }`}
          >
            <FiPlus size={20} />
            <span>Create Task</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExportCSV}
            className={`px-5 py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all border ${
              isDark 
                ? 'bg-gray-700 text-white border-gray-600 hover:bg-gray-600' 
                : 'bg-white/10 text-white border-white/20 active:bg-white/20'
            }`}
          >
            <FiDownload size={20} />
            <span>Export CSV</span>
          </motion.button>
        </div>
        )}
      </div>

      {/* Everyone's My Tasks (admin only) */}
      {isAdmin && tasksPageView === 'everyone_my_tasks' && (
        <div className={`backdrop-blur-md rounded-xl border overflow-hidden ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white/10 border-white/20'
        }`}>
          {allUsersLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`} />
            </div>
          ) : allUsersTasks.length === 0 ? (
            <p className={`py-8 text-center ${isDark ? 'text-gray-400' : 'text-white/60'}`}>No personal tasks from any user yet.</p>
          ) : (
            <div className="divide-y divide-white/10">
              {allUsersTasks.map(({ id: userId, name, email, tasks: userTasks }) => (
                <div key={userId} className="p-4 lg:p-6">
                  <h2 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-white'}`}>
                    {name}
                  </h2>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-white/60'}`}>{email}</p>
                  {userTasks.length === 0 ? (
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-white/50'}`}>No personal tasks</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-white/20'}`}>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Title</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Status</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Priority</th>
                            <th className={`px-3 py-2 text-left font-semibold ${isDark ? 'text-gray-300' : 'text-white/80'}`}>Due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userTasks.map((task) => (
                            <tr key={task.id} className={`border-b ${isDark ? 'border-gray-700/50' : 'border-white/10'}`}>
                              <td className="px-3 py-2">
                                <span className={`${isDark ? 'text-white' : 'text-white'} flex items-center gap-2`}>
                                  {task.title}
                                  {task.recurrence_parent_id && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/30 text-indigo-200">Monthly</span>
                                  )}
                                </span>
                                {task.description && (
                                  <p className={`text-xs mt-0.5 line-clamp-1 ${isDark ? 'text-gray-400' : 'text-white/60'}`}>{task.description}</p>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className={`${task.status === 'completed' ? 'bg-green-500' : task.status === 'in_progress' ? 'bg-blue-500' : 'bg-gray-500'} text-white text-xs px-2 py-0.5 rounded-full capitalize`}>
                                  {task.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td className={`px-3 py-2 capitalize ${isDark ? 'text-gray-300' : 'text-white/80'}`}>{task.priority}</td>
                              <td className={`px-3 py-2 ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
                                {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Bar + filters + table/calendar - only for team tasks */}
      {tasksPageView === 'team' && (
      <>
      <div className="relative">
        <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-white/60'}`} size={20} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search tasks by title or description..."
          className={`w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 ${
            isDark 
              ? 'bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:ring-blue-500' 
              : 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:ring-white/50'
          }`}
        />
      </div>

      {/* Task Type Tabs */}
      <div className={`backdrop-blur-md rounded-xl p-2 border ${
        isDark 
          ? 'bg-gray-800/50 border-gray-700' 
          : 'bg-white/10 border-white/20'
      }`}>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Tasks' },
            { value: 'assigned_to_me', label: 'Assigned to Me' },
            { value: 'assigned_by_me', label: 'Assigned by Me' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all ${
                filterType === tab.value
                  ? isDark
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-primary-600 shadow-lg'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
              viewMode === 'table'
                ? isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-primary-600'
                : isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <FiList size={18} />
            <span>Table</span>
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
              viewMode === 'calendar'
                ? isDark
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-primary-600'
                : isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <FiCalendar size={18} />
            <span>Calendar</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'in_progress', 'completed', 'blocked'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2.5 rounded-xl font-medium transition-all capitalize ${
                filter === status
                  ? isDark
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-primary-600 shadow-lg'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white/10 text-white/80 active:bg-white/20'
              }`}
            >
              {status.replace('_', ' ')}
            </button>
          ))}
        </div>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className={`px-4 py-2.5 rounded-xl font-medium focus:outline-none focus:ring-2 ${
            isDark 
              ? 'bg-gray-800 border border-gray-700 text-white focus:ring-blue-500' 
              : 'bg-white/10 border border-white/20 text-white focus:ring-white/50'
          }`}
        >
          <option value="all">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => {
                  if (selectedTags.includes(tag)) {
                    setSelectedTags(selectedTags.filter(t => t !== tag));
                  } else {
                    setSelectedTags([...selectedTags, tag]);
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  selectedTags.includes(tag)
                    ? isDark
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-primary-600'
                    : isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-white/10 text-white/80 hover:bg-white/20'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tasks View - Calendar or Table */}
      {viewMode === 'calendar' ? (
        <TaskCalendar tasks={tasks} />
      ) : tasks.length === 0 ? (
        <div className={`backdrop-blur-md rounded-xl p-12 border text-center ${
          isDark 
            ? 'bg-gray-800/50 border-gray-700' 
            : 'bg-white/10 border-white/20'
        }`}>
          <p className={`text-lg mb-2 ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
            No tasks found
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-white/60'}`}>
            {isAdmin ? 'Create your first task to get started' : 'You have no tasks assigned'}
          </p>
        </div>
      ) : (
        <>
          <div className={`backdrop-blur-md rounded-xl border overflow-hidden ${
            isDark 
              ? 'bg-gray-800/50 border-gray-700' 
              : 'bg-white/10 border-white/20'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${
                    isDark ? 'border-gray-700' : 'border-white/20'
                  }`}>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Title</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Team</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Assigned To</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Status</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Priority</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Due Date</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${
                      isDark ? 'text-gray-300' : 'text-white'
                    }`}>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTasks.map((task, index) => (
                    <motion.tr
                      key={task.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`border-b hover:bg-opacity-50 transition-colors ${
                        isDark 
                          ? 'border-gray-700 hover:bg-gray-700/50' 
                          : 'border-white/10 hover:bg-white/5'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link
                            to={`/tasks/${task.id}`}
                            className={`font-medium hover:underline ${
                              isDark ? 'text-blue-400' : 'text-white'
                            }`}
                          >
                            {task.title}
                          </Link>
                          {task.recurrence_parent_id && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200">Monthly</span>
                          )}
                        </div>
                        {task.description && (
                          <p className={`text-xs mt-1 line-clamp-1 ${
                            isDark ? 'text-gray-400' : 'text-white/60'
                          }`}>
                            {task.description}
                          </p>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${
                        isDark ? 'text-gray-300' : 'text-white/80'
                      }`}>
                        {task.team_name || '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm ${
                        isDark ? 'text-gray-300' : 'text-white/80'
                      }`}>
                        {task.assignees && Array.isArray(task.assignees) && task.assignees.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {task.assignees.slice(0, 2).map((assignee, idx) => (
                              <span
                                key={assignee.id || idx}
                                className="inline-block px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-200"
                              >
                                {assignee.name}
                              </span>
                            ))}
                            {task.assignees.length > 2 && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isDark ? 'text-gray-400' : 'text-white/60'
                              }`}>
                                +{task.assignees.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          task.assigned_to_name || '-'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`${getStatusColor(task.status)} text-white text-xs px-2 py-1 rounded-full capitalize`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {task.priority && (
                          <span className={`${getPriorityColor(task.priority)} text-white text-xs px-2 py-1 rounded-full capitalize`}>
                            {task.priority}
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${
                        isDark ? 'text-gray-300' : 'text-white/80'
                      }`}>
                        {task.due_date ? format(new Date(task.due_date), 'MMM dd, yyyy') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(task.tags) ? task.tags : []).slice(0, 2).map((tag, idx) => {
                            const tagName = typeof tag === 'string' ? tag : (tag.tag || tag);
                            const tagColor = typeof tag === 'object' && tag.color ? tag.color : '#6366f1';
                            return (
                              <span
                                key={idx}
                                className="text-xs px-2 py-0.5 rounded text-white"
                                style={{ backgroundColor: tagColor }}
                              >
                                {tagName}
                              </span>
                            );
                          })}
                          {(Array.isArray(task.tags) ? task.tags : []).length > 2 && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isDark ? 'text-gray-400' : 'text-white/60'
                            }`}>
                              +{(Array.isArray(task.tags) ? task.tags : []).length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`flex items-center justify-between backdrop-blur-md rounded-xl p-4 border ${
              isDark 
                ? 'bg-gray-800/50 border-gray-700' 
                : 'bg-white/10 border-white/20'
            }`}>
              <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-white/80'}`}>
                Showing {startIndex + 1} to {Math.min(endIndex, tasks.length)} of {tasks.length} tasks
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === 1
                      ? isDark
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <FiChevronsLeft size={18} />
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === 1
                      ? isDark
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <FiChevronLeft size={18} />
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          currentPage === pageNum
                            ? isDark
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-primary-600'
                            : isDark
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-white/10 text-white/80 hover:bg-white/20'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === totalPages
                      ? isDark
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <FiChevronRight size={18} />
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-all ${
                    currentPage === totalPages
                      ? isDark
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-white/5 text-white/30 cursor-not-allowed'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  <FiChevronsRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
      </>
      )}

      <AnimatePresence>
        {showModal && (
          <TaskModal
            onClose={() => {
              setShowModal(false);
              fetchTasks();
            }}
            onSuccess={fetchTasks}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tasks;
